import alertsData from '../lib/alerts/index.jsx';
import {AlertTypes, AlertLevels} from '../lib/alerts/index.jsx';
import extensionData from '../lib/libraries/extensions/index.jsx';

const SHOW_ALERT = 'scratch-gui/alerts/SHOW_ALERT';
const SHOW_EXTENSION_ALERT = 'scratch-gui/alerts/SHOW_EXTENSION_ALERT';
const SHOW_MESSAGE_ALERT = 'scratch-gui/alerts/SHOW_MESSAGE_ALERT';
const CLOSE_ALERT = 'scratch-gui/alerts/CLOSE_ALERT';
const CLOSE_ALERTS_WITH_ID = 'scratch-gui/alerts/CLOSE_ALERTS_WITH_ID';
const CLOSE_ALERT_WITH_ID = 'scratch-gui/alerts/CLOSE_ALERT_WITH_ID';

/**
 * How long an ad-hoc message toast stays on screen, per level (ms).
 * Warnings get longer than status messages because they carry more to read.
 * The countdown pauses while the pointer is over the toast, so these are
 * "reading time if ignored", not a hard limit. Pass an explicit duration for
 * messages that ask the user to go fix something (see DURATION_FIX_REQUIRED).
 */
const MESSAGE_DURATIONS = {
    [AlertLevels.INFO]: 4000,
    [AlertLevels.SUCCESS]: 5000,
    [AlertLevels.WARN]: 10000
};

/** For errors the user must act on (fix a block, fill a field) — needs longer. */
const DURATION_FIX_REQUIRED = 15000;

/** Most ad-hoc toasts shown at once; oldest beyond this are dropped. */
const MAX_MESSAGE_ALERTS = 3;

/**
 * Initial state of alerts reducer
 *
 * {bool} visible - whether the alerts are visible
 * {array} alertsList - list of alerts, each with properties:
 *  * alertType (required): one of AlertTypes
 *  * closeButton (optional): bool indicating that we should show close button
 *  * content (optional): react element (a <FormattedMessage />)
 *  * extentionId (optional): id string that identifies the extension
 *  * iconURL (optional): string
 *  * level (required): string, one of AlertLevels
 *  * message (optional): string
 *  * showReconnect (optional): bool
 */
const initialState = {
    visible: true,
    alertsList: []
};

const filterPopupAlerts = alertsList => (
    alertsList.filter(curAlert => (
        curAlert.alertType === AlertTypes.STANDARD ||
        curAlert.alertType === AlertTypes.EXTENSION
    ))
);

const filterInlineAlerts = alertsList => (
    alertsList.filter(curAlert => (
        curAlert.alertType === AlertTypes.INLINE
    ))
);

const reducer = function (state, action) {
    if (typeof state === 'undefined') state = initialState;
    switch (action.type) {
    case SHOW_ALERT: { // intended to show standard and inline alerts, but not extensions
        const alertId = action.alertId;
        if (alertId) {
            const newAlert = {
                alertId: alertId,
                level: AlertLevels.WARN // default level
            };
            const alertData = alertsData.find(thisAlertData => thisAlertData.alertId === alertId);
            if (alertData) {
                const newList = state.alertsList.filter(curAlert => (
                    !alertData.clearList || alertData.clearList.indexOf(curAlert.alertId) === -1
                ));
                if (action.data && action.data.message) {
                    newAlert.message = action.data.message;
                }

                newAlert.alertType = alertData.alertType || AlertTypes.STANDARD;
                newAlert.closeButton = alertData.closeButton;
                newAlert.content = alertData.content;
                newAlert.iconURL = alertData.iconURL;
                newAlert.iconSpinner = alertData.iconSpinner;
                newAlert.level = alertData.level;
                newAlert.showDownload = alertData.showDownload;
                newAlert.showSaveNow = alertData.showSaveNow;

                newList.push(newAlert);
                return Object.assign({}, state, {
                    alertsList: newList
                });
            }
        }
        return state; // if alert not found, show nothing
    }
    case SHOW_EXTENSION_ALERT: {
        const extensionId = action.data.extensionId;
        if (extensionId) {
            const extension = extensionData.find(ext => ext.extensionId === extensionId);
            if (extension) {
                const newList = state.alertsList.slice();
                const newAlert = {
                    alertType: AlertTypes.EXTENSION,
                    closeButton: true,
                    extensionId: extensionId,
                    extensionName: extension.name,
                    iconURL: extension.connectionSmallIconURL,
                    level: AlertLevels.WARN,
                    showReconnect: true
                };
                newList.push(newAlert);

                return Object.assign({}, state, {
                    alertsList: newList
                });
            }
        }
        return state; // if alert not found, show nothing
    }
    case SHOW_MESSAGE_ALERT: {
        // Show an ad-hoc alert with a plain-text message and a chosen level.
        // Used to replace native window.alert() calls (e.g. device upload flow).
        const {message, level, id, duration, shownAt} = action.data;
        if (!message) return state;
        const alertLevel = level || AlertLevels.WARN;
        const alertId = id || `message-${alertLevel}`;
        // Replace any existing alert with the same id so messages don't stack up.
        let newList = state.alertsList.filter(curAlert => curAlert.alertId !== alertId);
        newList.push({
            alertId: alertId,
            alertType: AlertTypes.STANDARD,
            closeButton: true,
            content: message,
            level: alertLevel,
            // ms before this toast auto-closes (0 = stay until dismissed). The
            // countdown lives in the Alert component so it can pause on hover.
            duration: typeof duration === 'number' ? duration : MESSAGE_DURATIONS[alertLevel],
            isMessage: true,
            shownAt: shownAt
        });
        // Cap how many ad-hoc toasts can stack up; drop the oldest beyond the cap
        // so a burst of messages can never bury the workspace.
        const messageAlerts = newList.filter(a => a.isMessage);
        if (messageAlerts.length > MAX_MESSAGE_ALERTS) {
            const dropIds = messageAlerts
                .slice(0, messageAlerts.length - MAX_MESSAGE_ALERTS)
                .map(a => a.alertId);
            newList = newList.filter(a => dropIds.indexOf(a.alertId) === -1);
        }
        return Object.assign({}, state, {
            alertsList: newList
        });
    }
    case CLOSE_ALERT_WITH_ID:
    case CLOSE_ALERT: {
        if (action.alertId) {
            action.index = state.alertsList.findIndex(a => a.alertId === action.alertId);
            if (action.index === -1) return state;
        }
        const newList = state.alertsList.slice();
        newList.splice(action.index, 1);
        return Object.assign({}, state, {
            alertsList: newList
        });
    }
    case CLOSE_ALERTS_WITH_ID: {
        return Object.assign({}, state, {
            alertsList: state.alertsList.filter(curAlert => (
                curAlert.alertId !== action.alertId
            ))
        });
    }
    default:
        return state;
    }
};

/**
 * Action creator to close an alert with the given index.
 *
 * @param {object} index - the index of the alert to close.
 * @return {object} - an object to be passed to the reducer.
 */
const closeAlert = function (index) {
    return {
        type: CLOSE_ALERT,
        index
    };
};

/**
 * Action creator to close all alerts with a given ID.
 *
 * @param {string} alertId - id string of the alert to close
 * @return {object} - an object to be passed to the reducer.
 */
const closeAlertsWithId = function (alertId) {
    return {
        type: CLOSE_ALERTS_WITH_ID,
        alertId
    };
};

/**
 * Action creator to close a single alert with a given ID.
 *
 * @param {string} alertId - id string of the alert to close
 * @return {object} - an object to be passed to the reducer.
 */
const closeAlertWithId = function (alertId) {
    return {
        type: CLOSE_ALERT_WITH_ID,
        alertId
    };
};

/**
 * Action creator to show an alert with the given alertId.
 *
 * @param {string} alertId - id string of the alert to show
 * @return {object} - an object to be passed to the reducer.
 */
const showStandardAlert = function (alertId) {
    return {
        type: SHOW_ALERT,
        alertId
    };
};

/**
 * Action creator to show an alert with the given input data.
 *
 * @param {object} data - data for the alert
 * @param {string} data.message - message for the alert
 * @param {string} data.extensionId - extension ID for the alert
 * @return {object} - an object to be passed to the reducer.
 */
const showExtensionAlert = function (data) {
    return {
        type: SHOW_EXTENSION_ALERT,
        data
    };
};

/**
 * Function to dispatch showing an alert, with optional
 * timeout to make it close/go away.
 *
 * @param {object} dispatch - dispatch function
 * @param {string} alertId - the ID of the alert
 */
const showAlertWithTimeout = function (dispatch, alertId) {
    const alertData = alertsData.find(thisAlertData => thisAlertData.alertId === alertId);
    if (alertData) {
        dispatch(showStandardAlert(alertId));
        if (alertData.maxDisplaySecs) {
            setTimeout(() => {
                dispatch(closeAlertsWithId(alertId));
            }, alertData.maxDisplaySecs * 1000);
        }
    }
};

/**
 * Action creator to show an ad-hoc alert with a plain-text message.
 *
 * @param {object} data - data for the alert
 * @param {string} data.message - message text to display
 * @param {string} data.level - one of AlertLevels (defaults to WARN)
 * @param {string} data.id - optional id so repeated messages replace rather than stack
 * @return {object} - an object to be passed to the reducer.
 */
const showMessageAlert = function (data) {
    return {
        type: SHOW_MESSAGE_ALERT,
        // shownAt makes each showing distinct so the UI can give a re-shown
        // message a fresh component instance (and therefore a fresh countdown).
        data: Object.assign({}, data, {shownAt: Date.now()})
    };
};

/**
 * Dispatch a plain-text message alert, auto-dismissing after a timeout.
 * Success messages default to a 5s auto-dismiss; warnings/info stay until closed.
 *
 * @param {function} dispatch - redux dispatch function
 * @param {object} options - {message, level, id, timeoutSecs}
 */
const showMessageAlertWithTimeout = function (dispatch, {message, level, id, timeoutSecs} = {}) {
    if (!message) return;
    const alertLevel = level || AlertLevels.WARN;
    const alertId = id || `message-${alertLevel}`;
    // Auto-dismiss is owned by the Alert component (so it can pause while the
    // pointer is over the toast); we only decide how long it should run.
    // timeoutSecs overrides the per-level default; 0 means "stay until dismissed".
    const duration = typeof timeoutSecs === 'number' ?
        timeoutSecs * 1000 :
        MESSAGE_DURATIONS[alertLevel];
    dispatch(showMessageAlert({message, level: alertLevel, id: alertId, duration}));
};

export {
    reducer as default,
    initialState as alertsInitialState,
    closeAlert,
    closeAlertWithId,
    filterInlineAlerts,
    filterPopupAlerts,
    showAlertWithTimeout,
    showExtensionAlert,
    showMessageAlert,
    showMessageAlertWithTimeout,
    showStandardAlert,
    DURATION_FIX_REQUIRED,
    MESSAGE_DURATIONS
};
